import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import './BugTitleTooltip.css'

const HOVER_DELAY_MS = 1000

type Placement = { left: number; top?: number; bottom?: number }

// The full title and summary of a bug row, which the row itself cuts with an ellipsis. Shown only after a
// deliberate hover so it never flashes while the pointer crosses the table. Portaled because the title
// cell clips its overflow.
export default function BugTitleTooltip({ title, summary, className, children }: {
  title: string
  summary?: string | null
  className?: string
  children: ReactNode
}) {
  const [placement, setPlacement] = useState<Placement | null>(null)
  const timer = useRef<number | null>(null)

  const clearTimer = () => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = null
  }

  useEffect(() => clearTimer, [])

  useEffect(() => {
    if (!placement) return
    const hide = () => setPlacement(null)
    window.addEventListener('scroll', hide, true)
    window.addEventListener('mousedown', hide, true)
    return () => {
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('mousedown', hide, true)
    }
  }, [placement])

  const onMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    clearTimer()
    timer.current = window.setTimeout(() => {
      const rect = target.getBoundingClientRect()
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - 488))
      // Opens below the cell, or above it when the row sits near the bottom of the viewport.
      setPlacement(rect.bottom + 180 > window.innerHeight
        ? { left, bottom: window.innerHeight - rect.top + 6 }
        : { left, top: rect.bottom + 6 })
    }, HOVER_DELAY_MS)
  }

  const onMouseLeave = () => {
    clearTimer()
    setPlacement(null)
  }

  const text = summary?.trim()
  return (
    <div className={className} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {children}
      {placement && createPortal(
        <div className="bug-title-tooltip" role="tooltip" style={placement} data-testid="bug-title-tooltip">
          <div className="bug-title-tooltip__title">{title}</div>
          {text && <div className="bug-title-tooltip__summary">{text}</div>}
        </div>,
        document.body,
      )}
    </div>
  )
}
